import React, { memo, useState } from 'react'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { ISourcedCarrierRate } from 'types/graphqlTypes'
import { IRFPLaneRate } from 'apps/marketplace/utils/api/rfpLaneRates'
import { ShipperRateObjectAugmented } from 'apps/marketplace/utils/hooks/AwardedLanes/useAwardedLanesApi'
import { AddRateModal } from '../../../rfp-lanes/AddRateModal'
import { DeleteRateConfirmationModal } from '../../../rfp-lanes/DeleteRateConfirmationModal'
import { EditRateModal } from '../../../rfp-lanes/EditRateModal'
import { LaneHeader } from '../LaneHeader'
import { AwardedLaneModalActions } from './AwardedLaneModalActions'
import SourcedCarrierRatesTable from './SourcedCarrierRatesTable'
import { useSourcedCarrierRatesQuery } from './graphql/SourcedCarrierRates'
import { useFilteredSourcedCarrierRates } from './hooks'

const useRatesTabContentStyles = makeStyles({
  card: {
    '& header': {
      width: '100%',
      '& div': {
        width: '100%',
      },
    },
  },
  cardTitle: {
    display: 'inline-flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  gridItem: {
    flexGrow: 1,
  },
  container: {
    margin: 0,
    paddingRight: 12,
  },
})

interface IRatesTabContentProps {
  currentRate: ShipperRateObjectAugmented
}

export const RatesTabContentProps = memo<IRatesTabContentProps>(({ currentRate }) => {
  const [showAddRateModal, setShowAddRateModal] = useState<boolean>(false)
  const [rfpLaneRate, setRfpLaneRate] = useState<IRFPLaneRate | null>(null)
  const [deleteRfpLaneRate, setDeleteRfpLaneRate] = useState<IRFPLaneRate | null>(null)
  const [deleteRfpLaneRates] = useState<IRFPLaneRate[]>([])

  const { openToast } = useGlobalToast()
  const classes = useRatesTabContentStyles()
  const { id: shipperRateId } = currentRate || {}

  const {
    data: sourcedCarrierRatesDataNewApi,
    loading: loadingSourcedCarrierRatesNewApi,
    refetch: refetchSourcedCarrierRatesNewApi,
  } = useSourcedCarrierRatesQuery({
    variables: {
      shipperRateId: String(currentRate.id),
    },
  })
  const sourcedCarrierRatesNewApi =
    sourcedCarrierRatesDataNewApi?.sourcedCarrierRates.nodes?.map(
      node => node as ISourcedCarrierRate
    ) ?? []

  const {
    fmsRates: fmsRatesNewApi,
    nonFmsRates: nonFmsRatesNewApi,
  } = useFilteredSourcedCarrierRates(sourcedCarrierRatesNewApi)

  const closeAction = (type?: string) => {
    if (type) {
      let message = ''
      if (type === 'success') message = 'Successfully updated rate.'
      if (type === 'delete-success') message = 'Successfully deleted rate.'
      if (message) {
        openToast({
          toastMessage: message,
          toastType: 'default',
        })
      }
    }
    setRfpLaneRate(null)
    setDeleteRfpLaneRate(null)
    setShowAddRateModal(false)
  }

  const awardedLaneHeader = (
    <Box pt={3} pl={3}>
      <Typography variant='h3'>
        <LaneHeader rate={currentRate} />
      </Typography>
    </Box>
  )

  return (
    <>
      <Grid container spacing={3}>
        <Grid className={classes.gridItem} item xs={12}>
          <AwardedLaneModalActions currentRate={currentRate} />
        </Grid>

        <Grid className={classes.gridItem} item xs={12}>
          <SourcedCarrierRatesTable
            enableAddButton
            loading={loadingSourcedCarrierRatesNewApi}
            refetch={refetchSourcedCarrierRatesNewApi}
            shipperRateId={String(shipperRateId)}
            sourcedCarrierRates={nonFmsRatesNewApi}
            title='Sourced Rates'
          />
        </Grid>

        <Grid className={classes.gridItem} item xs={12}>
          <SourcedCarrierRatesTable
            loading={loadingSourcedCarrierRatesNewApi}
            refetch={refetchSourcedCarrierRatesNewApi}
            shipperRateId={String(shipperRateId)}
            sourcedCarrierRates={fmsRatesNewApi}
            title='Dedicated Lane Bids'
            dataTest='dedicated-lane-bids'
          />
        </Grid>
      </Grid>

      {rfpLaneRate && (
        <EditRateModal
          rfpLaneRate={rfpLaneRate}
          closeAction={closeAction}
          awardedLaneHeader={awardedLaneHeader}
        />
      )}
      {(deleteRfpLaneRate || deleteRfpLaneRates.length > 0) && (
        <DeleteRateConfirmationModal
          rfpLaneRate={deleteRfpLaneRate}
          rfpLaneRates={deleteRfpLaneRates}
          closeAction={closeAction}
        />
      )}
      {showAddRateModal && (
        <AddRateModal
          closeAction={closeAction}
          awardedLane={currentRate}
          awardedLaneHeader={awardedLaneHeader}
        />
      )}
    </>
  )
})

export default RatesTabContentProps
